import {Color} from '@benchling/nucleus-ui';
import {createStyles, Group, Text} from '@mantine/core';
import React from 'react';

import {
  ProcedureRunDataEntryMode,
  ProcedureType,
  StudyType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcedureChip from 'ui-library/chips/ProcedureChip';

import MultiBatchSelector from '../process-development/multi-batch-selector/MultiBatchSelector';
import {getTypedStudyAdapter} from '../TypedStudyAdapters';
import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {EditNumPlannedConditionsButtons} from './EditNumPlannedConditionsButtons';
import {ProcedureChangeParams} from './EditProcedureModal';
import MethodExecutionLimitError from './MethodExecutionLimitError';
import ProcedureCard from './ProcedureCard';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {TransposedReplicateTable} from './TransposedReplicateTable';
import {Condition, ConditionGroup} from './useExperimentDesignState';
import {MethodExecutionLimitState} from './useMethodExecutionLimitState';

interface BaseProcedureRunDesignEditorLeftSidepanelProps<TCondition> {
  isPlan: boolean;
  conditions: Array<TCondition>;
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (experimentalInputs: ExperimentalInputs) => void;
  editState: StudyDesignTabEditState;
  procedureType: ProcedureType;
  methodExecutionLimitState: MethodExecutionLimitState;
  studyType: StudyType;
  dataEntryMode: ProcedureRunDataEntryMode;
  procedureId: string;
  flowchartVersionId: string;
}

interface ProcedureRunDesignEditorLeftSidepanelPlanProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<ConditionGroup> {
  isPlan: true;
  onProcedureChange: (params: ProcedureChangeParams) => Promise<void>;
  setDataEntryMode: (dataEntryMode: ProcedureRunDataEntryMode) => void;
  hasUpdateAvailable: boolean;
  onOpenUpdateModal: () => void;
}

interface ProcedureRunDesignEditorLeftSidepanelRunProps extends BaseProcedureRunDesignEditorLeftSidepanelProps<Condition> {
  isPlan: false;
}

type ProcedureRunDesignEditorLeftSidepanelProps =
  | ProcedureRunDesignEditorLeftSidepanelPlanProps
  | ProcedureRunDesignEditorLeftSidepanelRunProps;

export default function ProcedureRunDesignEditorLeftSidepanel(
  props: ProcedureRunDesignEditorLeftSidepanelProps
): JSX.Element {
  const {classes} = useStyles();
  const onProcedureChange = props.isPlan ? props.onProcedureChange : async () => {};
  const isLocked = props.editState !== StudyDesignTabEditState.EDITING;
  const isEditable = props.isPlan && !isLocked;
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
    stringCase: StringCase.LOWER,
  });
  const editNumColsButtons = props.isPlan ? (
    <EditNumPlannedConditionsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
      showReplicateLimitLabel={false}
    />
  ) : (
    <EditNumConditionsButtons
      conditions={props.conditions}
      setConditions={props.setConditions}
      methodExecutionLimitState={props.methodExecutionLimitState}
      procedureType={props.procedureType}
      showReplicateLimitLabel={false}
    />
  );
  const replicateTableComponent = (
    <>
      <TransposedReplicateTable
        conditions={props.conditions}
        setConditions={props.setConditions}
        experimentalInputs={props.experimentalInputs}
        setExperimentalInputs={props.setExperimentalInputs}
        editNumColsButtons={editNumColsButtons}
        locked={isLocked}
        procedureType={props.procedureType}
        isPlan={props.isPlan}
        dataTestAttrs={{component: 'TransposedReplicateTable', element: 'replicateTable'}}
      />
      {props.methodExecutionLimitState.isOverLimit && (
        <MethodExecutionLimitError
          replicateLimit={props.methodExecutionLimitState.replicateLimit}
          procedureType={props.procedureType}
        />
      )}
    </>
  );

  if (props.isPlan) {
    return (
      <div className={classes.sidepanelContent}>
        {replicateTableComponent}
        <div className={classes.procedureCardWrapper}>
          <ProcedureCard
            procedureId={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
            procedureType={props.procedureType}
            studyType={props.studyType}
            onProcedureChange={onProcedureChange}
            isEditable={isEditable}
            hasUpdateAvailable={props.hasUpdateAvailable}
            onOpenUpdateModal={props.onOpenUpdateModal}
          />
        </div>
        {getTypedStudyAdapter(props.studyType).isMultiBatchEnabled && (
          <div className={classes.multiBatchSelectorWrapper}>
            <MultiBatchSelector
              dataEntryMode={props.dataEntryMode}
              onSelectDataEntryMode={props.setDataEntryMode}
              isDisabled={!isEditable}
              withContainerPadding={false}
            />
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={classes.sidepanelContent}>
      {replicateTableComponent}
      <div className={classes.runSectionWrapper}>
        <Group spacing="xs" noWrap={true} className={classes.activeProcedureRow}>
          <Text className={classes.activeProcedureLabel}>{`Active ${procedureString}:`}</Text>
          <ProcedureChip
            size={ChipSize.MINI}
            id={props.procedureId}
            flowchartVersionId={props.flowchartVersionId}
          />
        </Group>
      </div>
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  sidepanelContent: {
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
  },
  procedureCardWrapper: {
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: theme.spacing.md,
  },
  multiBatchSelectorWrapper: {
    marginTop: theme.spacing.xl,
  },
  runSectionWrapper: {
    marginTop: theme.spacing.md,
    borderTop: `1px solid ${Color.GRAY_200}`,
    paddingTop: theme.spacing.md,
  },
  activeProcedureRow: {
    marginTop: 0,
  },
  activeProcedureLabel: {
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '14px',
  },
}));
